import en from '../locales/en.json'
import pl from '../locales/pl.json'
import { pluralFormOf } from './plurals'

export const LOCALES = ['en', 'pl'] as const

export type Locale = (typeof LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'pl'

const CATALOGUES: Record<Locale, unknown> = { en, pl }

export const isLocale = (value: unknown): value is Locale =>
  LOCALES.includes(value as Locale)

export function localeOf(value: unknown): Locale {
  return isLocale(value) ? value : DEFAULT_LOCALE
}

function lookup(catalogue: unknown, key: string): string | null {
  let node: unknown = catalogue

  for (const segment of key.split('.')) {
    if (node === null || typeof node !== 'object') return null

    node = (node as Record<string, unknown>)[segment]
  }

  return typeof node === 'string' ? node : null
}

export type Vars = Record<string, string | number>

function interpolate(template: string, vars: Vars): string {
  return template.replace(/\{\{(\w+)\}\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  )
}

export function translate(locale: Locale, key: string, vars: Vars = {}): string {
  const found = lookup(CATALOGUES[locale], key) ?? lookup(CATALOGUES[DEFAULT_LOCALE], key)

  return found === null ? key : interpolate(found, vars)
}

export function pluralize(locale: Locale, key: string, count: number, vars: Vars = {}): string {
  const form = pluralFormOf(locale, count)
  const scoped = `${key}.${form}`
  const found =
    lookup(CATALOGUES[locale], scoped) ??
    lookup(CATALOGUES[locale], `${key}.many`) ??
    lookup(CATALOGUES[DEFAULT_LOCALE], scoped) ??
    lookup(CATALOGUES[DEFAULT_LOCALE], `${key}.many`)

  return found === null ? scoped : interpolate(found, { ...vars, count })
}

export type Translator = (key: string, vars?: Vars) => string

export function translatorFor(locale: Locale): Translator {
  return (key, vars = {}) => translate(locale, key, vars)
}

export function localeFromHeader(header: string | null | undefined): Locale {
  for (const part of (header ?? '').split(',')) {
    const tag = part.split(';')[0].trim().toLowerCase().split('-')[0]

    if (isLocale(tag)) return tag
  }

  return DEFAULT_LOCALE
}

export { pluralFormOf, PLURAL_FORMS, type PluralForm } from './plurals'
export { dayKey, dayOf, moneyOf, stampedAt, today } from './formats'
